import type { PropsWithChildren } from 'react';
import { css, keyframes, styled } from 'styled-components';
import type { UseAnimationIterationOptions } from '../hooks';
import { useAnimationIteration } from '../hooks';
import { TRANSFER_ANIMATIONS } from './transfer-animations';

type Props = PropsWithChildren<UseAnimationIterationOptions>;

/**
 * Animate an element pulsing in and out. When the element first
 * transitions to pulsing, scale it down and then initiate the
 * pulse after the scale down animation completes.
 */
export function Pulse({ children, isIterating, onIteration }: Props) {
  let scale = 1;
  let transitionDuration = `${TRANSFER_ANIMATIONS.duration}ms`;
  let ease: string = TRANSFER_ANIMATIONS.ease;

  /**
   * When generating the thumb, we first animate down to the scale of
   * 0.5 and we apply a delay before initiating the pulse animation.
   */

  if (isIterating) {
    scale = 0.5;
    transitionDuration = `${SCALE_DOWN_DURATION}ms`;
    ease = TRANSFER_ANIMATIONS['generating-thumbnail'].ease;
  }

  const { ref } = useAnimationIteration({
    isIterating,
    onIteration,
  });

  return (
    <ScaleContainer
      style={{
        transform: `scale(${scale})`,
        transitionDuration,
        transitionTimingFunction: ease,
        transitionProperty: 'transform',
      }}
    >
      <StyledPulse ref={ref} $delay={SCALE_DOWN_DURATION} $pulse={isIterating}>
        {children}
      </StyledPulse>
    </ScaleContainer>
  );
}

const SCALE_DOWN_DURATION =
  TRANSFER_ANIMATIONS['generating-thumbnail'].duration;

const ScaleContainer = styled.div`
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  position: absolute;
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.3); }
  100% { transform: scale(1); }
`;

const StyledPulse = styled.div<{
  $delay: number;
  $pulse: boolean;
}>`
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;

  ${(p) => {
    return (
      p.$pulse &&
      css`
        animation: ${pulseAnimation} 1s ease-in-out infinite;
        animation-delay: ${p.$delay}ms;
        animation-fill-mode: forwards;
      `
    );
  }}
`;
